/**
 * Quick View modal
 * Opens product.quick-view alternate template into a shared dialog.
 */
(function () {
  const MONEY_FORMAT =
    (window.theme && window.theme.moneyFormat) ||
    (window.Shopify && Shopify.money_format) ||
    "${{amount}}";

  function formatMoney(cents) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    const value = (Number(cents) / 100).toFixed(2);
    const format =
      typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function"
        ? null
        : MONEY_FORMAT;

    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
      try {
        return Shopify.formatMoney(cents, MONEY_FORMAT);
      } catch (e) {
        /* fall through */
      }
    }

    if (!format) return `$${value}`;
    return format
      .replace(/\{\{\s*amount\s*\}\}/, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(cents) / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace(".", ","));
  }

  class QuickViewModal extends HTMLElement {
    constructor() {
      super();
      this.content = this.querySelector("#QuickViewContent");
      this.loadingEl = this.querySelector(".quick-view-modal__loading");
      this.dialog = this.querySelector(".quick-view-modal__dialog");
      this.activeOpener = null;
      this.onKeyDown = this.onKeyDown.bind(this);
      this.addEventListener("click", (event) => {
        if (event.target.closest("[data-qv-close]")) this.close();
      });
    }

    connectedCallback() {
      if (!this.hasAttribute("role")) this.setAttribute("role", "presentation");
    }

    open(opener) {
      this.activeOpener = opener || null;
      const url = opener && opener.getAttribute("data-product-url");
      if (!url) return;

      this.freezeBundleSwipers();

      this.setAttribute("open", "");
      this.classList.add("is-open");
      this.setAttribute("aria-hidden", "false");
      document.body.classList.add("quick-view-open");
      document.addEventListener("keydown", this.onKeyDown);
      this.showLoading(true);
      this.loadProduct(url, opener);
    }

    close() {
      // Prevent the closing click from falling through onto Bundle Builder
      // cards (which would select the product and trigger Swiper observer).
      this.suppressUnderlyingClicks();

      this.removeAttribute("open");
      this.classList.remove("is-open");
      this.setAttribute("aria-hidden", "true");
      document.body.classList.remove("quick-view-open");
      document.removeEventListener("keydown", this.onKeyDown);
      if (this.content) this.content.innerHTML = "";
      this.showLoading(false);
      this.teardownStickyActions();

      this.restoreBundleSwipers();

      // Do not restore focus into Swiper slides — it scrolls/reorders loop slides.
      this.activeOpener = null;
    }

    suppressUnderlyingClicks() {
      const suppress = (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      };
      document.addEventListener("click", suppress, true);
      document.addEventListener("pointerup", suppress, true);
      document.addEventListener("mouseup", suppress, true);
      window.setTimeout(() => {
        document.removeEventListener("click", suppress, true);
        document.removeEventListener("pointerup", suppress, true);
        document.removeEventListener("mouseup", suppress, true);
      }, 400);
    }

    freezeBundleSwipers() {
      this._swiperFreeze = [];
      document.querySelectorAll("#bundle-builder .mySwiper").forEach((el) => {
        const swiper = el.swiper;
        if (!swiper) return;
        this._swiperFreeze.push({
          swiper,
          realIndex: swiper.realIndex,
        });
        swiper.allowTouchMove = false;
        if (typeof swiper.disable === "function") swiper.disable();
      });
    }

    restoreBundleSwipers() {
      const snapshots = this._swiperFreeze || [];
      this._swiperFreeze = [];
      // Wait a frame so layout/overflow from modal close settles first.
      requestAnimationFrame(() => {
        snapshots.forEach(({ swiper, realIndex }) => {
          if (!swiper || swiper.destroyed) return;
          if (typeof swiper.enable === "function") swiper.enable();
          swiper.allowTouchMove = true;
          try {
            if (swiper.params && swiper.params.loop) {
              swiper.slideToLoop(realIndex, 0, false);
            } else {
              swiper.slideTo(realIndex, 0, false);
            }
          } catch (e) {
            /* ignore */
          }
        });
      });
    }

    onKeyDown(event) {
      if (event.key === "Escape") this.close();
    }

    showLoading(isLoading) {
      if (!this.loadingEl) return;
      this.loadingEl.hidden = !isLoading;
    }

    buildQuickViewUrl(productUrl) {
      try {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set("view", "quick-view");
        return url.pathname + url.search;
      } catch (e) {
        const joiner = productUrl.includes("?") ? "&" : "?";
        return `${productUrl}${joiner}view=quick-view`;
      }
    }

    loadProduct(productUrl, opener) {
      if (opener) {
        opener.setAttribute("aria-disabled", "true");
        opener.classList.add("loading");
      }

      fetch(this.buildQuickViewUrl(productUrl))
        .then((response) => {
          if (!response.ok) throw new Error("Quick View fetch failed");
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const root = doc.querySelector(".quick-view");
          if (!root || !this.content) throw new Error("Quick View markup missing");
          this.content.innerHTML = "";
          this.content.appendChild(document.importNode(root, true));
          this.initContent(this.content.querySelector(".quick-view"));
          this.showLoading(false);
          if (this.dialog) this.dialog.focus();
        })
        .catch((error) => {
          console.error("[QuickView]", error);
          this.showLoading(false);
          this.close();
        })
        .finally(() => {
          if (opener) {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
          }
        });
    }

    initContent(root) {
      if (!root) return;
      this.initGallery(root);
      this.initOptions(root);
      this.initColorSwatches(root);
      this.upgradeProductForm(root);
      this.initStickyActions(root);
    }

    initStickyActions(root) {
      this.teardownStickyActions();

      if (!window.matchMedia("(max-width: 989px)").matches) return;

      const actions = root.querySelector("[data-qv-actions]");
      const anchor = root.querySelector("[data-qv-actions-anchor]");
      if (!actions || !anchor) return;

      let spacer = root.querySelector(".quick-view__actions-spacer");
      if (!spacer) {
        spacer = document.createElement("div");
        spacer.className = "quick-view__actions-spacer";
        spacer.setAttribute("aria-hidden", "true");
        actions.insertAdjacentElement("afterend", spacer);
      }

      const setFixed = (fixed) => {
        actions.classList.toggle("is-fixed", fixed);
        spacer.classList.toggle("is-active", fixed);
        if (fixed) {
          spacer.style.height = `${actions.offsetHeight}px`;
        } else {
          spacer.style.height = "";
        }
      };

      // Keep CTA visible until its natural spot scrolls into view.
      setFixed(true);

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          setFixed(!entry.isIntersecting);
        },
        {
          root: this.dialog,
          threshold: 0.01,
          rootMargin: "0px 0px -8px 0px",
        },
      );

      observer.observe(anchor);
      this._stickyActions = { observer, actions, spacer, setFixed };

      window.setTimeout(() => {
        if (actions.classList.contains("is-fixed")) {
          spacer.style.height = `${actions.offsetHeight}px`;
        }
      }, 50);
    }

    teardownStickyActions() {
      if (!this._stickyActions) return;
      const { observer, actions, spacer, setFixed } = this._stickyActions;
      if (observer) observer.disconnect();
      if (typeof setFixed === "function") setFixed(false);
      if (actions) actions.classList.remove("is-fixed");
      if (spacer) {
        spacer.classList.remove("is-active");
        spacer.style.height = "";
      }
      this._stickyActions = null;
    }

    initGallery(root) {
      const thumbs = Array.from(root.querySelectorAll("[data-qv-thumb]"));
      const mainImg = root.querySelector("[data-qv-main-img]");
      const thumbsList = root.querySelector("[data-qv-thumbs]");
      if (!mainImg || !thumbs.length) return;

      let index = thumbs.findIndex((thumb) => thumb.classList.contains("is-active"));
      if (index < 0) index = 0;

      const setActive = (nextIndex) => {
        index = (nextIndex + thumbs.length) % thumbs.length;
        thumbs.forEach((thumb, i) => thumb.classList.toggle("is-active", i === index));
        const src = thumbs[index].getAttribute("data-src");
        if (src) mainImg.src = src;
        thumbs[index].scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      };

      thumbs.forEach((thumb, i) => {
        thumb.addEventListener("click", () => setActive(i));
      });

      const prev = root.querySelector("[data-qv-prev]");
      const next = root.querySelector("[data-qv-next]");
      if (prev) prev.addEventListener("click", () => setActive(index - 1));
      if (next) next.addEventListener("click", () => setActive(index + 1));

      const thumbsPrev = root.querySelector("[data-qv-thumbs-prev]");
      const thumbsNext = root.querySelector("[data-qv-thumbs-next]");
      if (thumbsList && thumbsPrev) {
        thumbsPrev.addEventListener("click", () => {
          thumbsList.scrollBy({ top: -80, behavior: "smooth" });
          setActive(index - 1);
        });
      }
      if (thumbsList && thumbsNext) {
        thumbsNext.addEventListener("click", () => {
          thumbsList.scrollBy({ top: 80, behavior: "smooth" });
          setActive(index + 1);
        });
      }
    }

    parseVariants(root) {
      const script = root.querySelector("[data-qv-variants]");
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return [];
      }
    }

    initOptions(root) {
      const variants = this.parseVariants(root);
      const optionGroups = Array.from(root.querySelectorAll("[data-qv-option]"));
      if (!optionGroups.length || !variants.length) return;

      const getSelectedOptions = () => {
        const selected = { 1: null, 2: null, 3: null };
        optionGroups.forEach((group) => {
          const position = Number(group.getAttribute("data-option-position"));
          const active = group.querySelector(
            ".is-active[data-qv-option-value], .selected[data-qv-option-value]",
          );
          if (active) selected[position] = active.getAttribute("data-qv-option-value");
        });
        return selected;
      };

      const findVariant = (selected) => {
        return variants.find((variant) => {
          return (
            (selected[1] == null || variant.option1 === selected[1]) &&
            (selected[2] == null || variant.option2 === selected[2]) &&
            (selected[3] == null || variant.option3 === selected[3])
          );
        });
      };

      const applyVariant = (variant) => {
        if (!variant) return;
        const idInput = root.querySelector("[data-qv-variant-id]");
        const priceEl = root.querySelector("[data-qv-current-price]");
        const monthlyEl = root.querySelector("[data-qv-monthly-price]");
        const atc = root.querySelector("[data-qv-atc]");
        const atcLabel = root.querySelector("[data-qv-atc-label]");
        const mainImg = root.querySelector("[data-qv-main-img]");

        if (idInput) {
          idInput.value = variant.id;
          idInput.disabled = !variant.available;
        }

        if (priceEl) {
          priceEl.innerHTML = `<strong>${formatMoney(variant.price)}</strong>`;
        }

        if (monthlyEl) {
          monthlyEl.innerHTML = `${formatMoney(Math.round(variant.price / 4))} <span>/mo</span>`;
        }

        if (atc) atc.disabled = !variant.available;
        if (atcLabel) atcLabel.textContent = variant.available ? "Add To Cart" : "Sold Out";

        if (variant.featured_image && mainImg) {
          mainImg.src = variant.featured_image;
        }
      };

      optionGroups.forEach((group) => {
        const label = group.querySelector("[data-qv-option-label]");
        group.querySelectorAll("[data-qv-option-value]").forEach((btn) => {
          btn.addEventListener("click", () => {
            group.querySelectorAll("[data-qv-option-value]").forEach((el) => {
              el.classList.remove("is-active", "selected");
              el.removeAttribute("aria-current");
            });
            btn.classList.add("is-active", "selected");
            btn.setAttribute("aria-current", "true");
            if (label) {
              label.textContent =
                btn.getAttribute("data-qv-option-value") || btn.textContent.trim();
            }
            applyVariant(findVariant(getSelectedOptions()));
          });
        });
      });
    }

    initColorSwatches(root) {
      root.querySelectorAll("[data-qv-product-url]").forEach((btn) => {
        btn.addEventListener("click", () => {
          if (
            btn.classList.contains("is-disabled") ||
            btn.classList.contains("selected") ||
            btn.classList.contains("is-active")
          ) {
            return;
          }
          const url = btn.getAttribute("data-qv-product-url");
          if (!url) return;
          this.showLoading(true);
          this.loadProduct(url, null);
        });
      });
    }

    upgradeProductForm(root) {
      // product-form custom element upgrades automatically;
      // product-form.js closes #QuickViewModal after a successful add.
      return root.querySelector("product-form");
    }
  }

  if (!customElements.get("quick-view-modal")) {
    customElements.define("quick-view-modal", QuickViewModal);
  }

  function getModal() {
    return document.getElementById("QuickViewModal");
  }

  function openFromButton(button) {
    const modal = getModal();
    if (!modal || typeof modal.open !== "function") return;
    modal.open(button);
  }

  // Capture phase so Bundle Builder card click (select item) never runs first.
  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest("[data-quick-view]");
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      openFromButton(button);
    },
    true,
  );

  // Expose for Bundle Builder cards created in JS
  window.QuickView = {
    open(url, opener) {
      const modal = getModal();
      if (!modal) return;
      if (opener) {
        opener.setAttribute("data-product-url", url);
        modal.open(opener);
      } else {
        const proxy = document.createElement("button");
        proxy.setAttribute("data-product-url", url);
        modal.open(proxy);
      }
    },
  };
})();
